import { useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { createUserSetting, updateUserSetting } from "api/userSettings";

import { DATA_TABLE_COLUMN_ORDER_SETTING } from "components/user-settings/constants/userSetting.constants";
import { DataTableColumnOrderEntry } from "components/user-settings/models";
import { useUserContext } from "components/user/context";
import { updateDataTableColumnOrderEntry } from "components/user/context/reducer/UserReducer";
import { useUserSettingsQueryKeys } from "components/user/queries";

/**
 * EVA-882: Persist the data-table column order for a given entityKind id
 * ("Well" or "Facility") to the user-svc setting.
 *
 * POST-first-then-PUT logic:
 *   - On first save for this entityKind (no in-context entry yet), POST to create.
 *   - On subsequent saves, PUT to update.
 *   - After a successful POST the returned entry (with id confirmed) is upserted
 *     into UserContext so future calls within the session route to PUT.
 *
 * IMPORTANT: POST-first-then-PUT is load-bearing. The backend PUT endpoint does
 * NOT upsert — it requires the resource to already exist. Always POST once before
 * any PUT for a given entityKind.
 *
 * Cross-instance guard (FIX 2): The primary POST-vs-PUT gate is the UserContext
 * entry populated via onSuccess. hasPostedRef is a same-tick guard only, to
 * prevent double-POST from rapid debounced calls before the first onSuccess fires.
 * It is intentionally per-hook-instance; true cross-tab/cross-widget deduplication
 * relies on the context entry being present after hydration (see useInitialUserContextLoader
 * which dedupes DataTableColumnOrderSetting entries by id on load).
 */
export function useDataTableColumnOrderMutation(entityKindId: string) {
  const [state, dispatch] = useUserContext();
  const queryClient = useQueryClient();

  // Track within this hook instance whether we have already POSTed for this
  // entityKind in the current session (optimistic guard against double-POST on
  // rapid debounced calls before the first PUT lands).
  const hasPostedRef = useRef(false);

  // Surface a save failure once per failing streak (reset on success) so a
  // user who reorders doesn't silently lose the order next session, without
  // spamming a toast on every debounced retry.
  const hasShownSaveErrorRef = useRef(false);

  // We read the context setting at call time inside mutateAsync — capture a
  // stable getter rather than a snapshot, so the ref is updated by React
  // before the next debounced call fires.
  const getContextEntry = useCallback(
    () =>
      (state?.settings?.dataTableColumnOrder ?? []).find((e) => e.id === entityKindId),
    [state?.settings?.dataTableColumnOrder, entityKindId]
  );

  const { mutate, isLoading, isError, error } = useMutation(
    `update-data-table-column-order-${entityKindId}`,
    async (properties: string[]) => {
      const payload: DataTableColumnOrderEntry = { id: entityKindId, properties };
      const contextEntry = getContextEntry();
      const alreadyExists = !!contextEntry || hasPostedRef.current;

      if (alreadyExists) {
        return updateUserSetting<DataTableColumnOrderEntry>(
          DATA_TABLE_COLUMN_ORDER_SETTING,
          payload
        );
      } else {
        // Mark before the request so concurrent debounced calls do not double-POST.
        hasPostedRef.current = true;
        return createUserSetting<DataTableColumnOrderEntry>(
          DATA_TABLE_COLUMN_ORDER_SETTING,
          payload
        );
      }
    },
    {
      onSuccess: (data) => {
        // Upsert the returned entry (which carries the canonical id) into context
        // so the next call in the same session routes to PUT.
        updateDataTableColumnOrderEntry(dispatch, data);
        hasShownSaveErrorRef.current = false;
        queryClient.invalidateQueries(useUserSettingsQueryKeys.userSettings);
      },
      onError: (err) => {
        // Roll back the optimistic post-flag so a retry can POST again.
        hasPostedRef.current = false;
        // eslint-disable-next-line no-console
        console.error("[DataTableColumnOrder] save failed", err);
        // Non-blocking, once per failing streak — the order still applies this
        // session but won't persist, so the loss isn't invisible.
        if (!hasShownSaveErrorRef.current) {
          hasShownSaveErrorRef.current = true;
          toast.error(
            "Couldn't save your column order. It may not be restored next session."
          );
        }
      }
    }
  );

  return { mutate, isLoading, isError, error };
}
